import { useEffect, useRef, useState } from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import type { GetServerSideProps } from "next";
import i18next from "i18next";
import "@/i18n/config";
import {
  LayoutDashboard,
  MessageCircle,
  Globe,
  Plus,
  Minus,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  CreditCard,
  Banknote,
  Link2,
} from "lucide-react";
import { useLanguage, LANGUAGES, type Lang } from "@/contexts/LanguageContext";
import { useTranslation } from "react-i18next";
import Image from "next/image";
import { DashboardCreateLink } from "@/components/dashboardCreateLink/dashboardCreateLink";

import MenoSteps from "@/components/menoSteps/menoSteps";
import MenoAnalytics from "@/components/menoAnalytics/menoAnalytics";
import MenoCompare from "@/components/menoCompare/menoCompare";
import MenoPricing from "@/components/menoPricing/menoPricing";
import BusinessType from "@/components/businessType/businessType";
import MenoRichItems from "@/components/menoRichItems/menoRichItems";
import MenoBrandLook from "@/components/menoBrandLook/menoBrandLook";
import MenoCompareV2 from "@/components/menoCompareV2/menoCompareV2";
import MenoTeamRoles from "@/components/menoTeamRoles/menoTeamRoles";
import MenoPricingV2 from "@/components/menoPricingV2/menoPricingV2";
import CustomSpinner from "@/components/spinner/spinner";

const Lottie = dynamic(() => import("lottie-react"), { ssr: false });

async function waitForLocaleReady() {
  if (typeof document !== "undefined" && document.fonts?.ready) {
    await document.fonts.ready;
  }
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

type PageProps = {
  initialSeoTitle: string;
  initialSeoDescription: string;
  initialOgDescription: string;
  currentUrl: string;
  canonicalBaseUrl: string;
};

/* ─── Animated Section Wrapper ─── */
function FadeInSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
      style={{
        willChange: "transform, opacity",
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
      }}
    >
      {children}
    </motion.div>
  );
}

/* ─── Commission Countdown (30% → 0%) ─── */
function CommissionCountdown({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const count = useMotionValue(100);
  const display = useTransform(count, (v) => `${Math.round(v)}%`);

  const radius = 150;
  const circumference = 2 * Math.PI * radius;
  const size = radius * 2 + 40;
  const center = size / 2;
  const strokeDashoffset = useTransform(
    count,
    (v) => circumference * (1 - v / 100),
  );

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(count, 0, {
      duration: 2.5,
      ease: [0.22, 1, 0.36, 1],
    });
    return controls.stop;
  }, [isInView, count]);

  return (
    <div
      ref={ref}
      className={`relative flex items-center justify-center ${className}`}
      aria-label={t("f6.commissionAriaLabel")}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="w-[220px] sm:w-[400px] lg:w-[460px] h-auto -rotate-90"
        aria-hidden="true"
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={18}
        />
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="white"
          strokeWidth={18}
          strokeLinecap="round"
          strokeDasharray={circumference}
          style={{ strokeDashoffset }}
        />
      </svg>

      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center"
        initial={{ scale: 0.85, opacity: 0 }}
        animate={isInView ? { scale: 1, opacity: 1 } : {}}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <motion.span
          className="text-[3.25rem] sm:text-[6.5rem] lg:text-[7.5rem] font-extrabold text-white tabular-nums tracking-tighter leading-none"
          style={{
            fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
          }}
          aria-live="polite"
        >
          {display}
        </motion.span>
        <span className="text-white/60 text-[10px] sm:text-base font-bold mt-2 sm:mt-4 uppercase tracking-[0.2em]">
          {t("f6.commission")}
        </span>
      </motion.div>
    </div>
  );
}

/* ─── FAQ Accordion Item ─── */
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5 text-start group"
      >
        <span className="text-base font-bold text-gray-900 sm:text-lg">
          {question}
        </span>
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FFE1EC] text-[#FF00A4] transition-colors duration-200"
          aria-hidden="true"
        >
          {isOpen ? (
            <Minus className="h-4 w-4" strokeWidth={2.5} />
          ) : (
            <Plus className="h-4 w-4" strokeWidth={2.5} />
          )}
        </span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="px-5 pb-5 text-sm leading-relaxed text-gray-500 sm:px-6 sm:pb-6 sm:text-base text-start">
          {answer}
        </div>
      </motion.div>
    </div>
  );
}

/* ─── Language Switcher ─── */
function LanguageSwitcher({
  variant = "default",
  onSwitchStart,
}: {
  variant?: "default" | "hero";
  onSwitchStart?: () => void;
}) {
  const { lang, setLang, isRTL } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = LANGUAGES.find((l) => l.code === lang)!;
  const isHero = variant === "hero";

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={
          isHero
            ? "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-white/95 transition-colors hover:bg-white/10 sm:text-sm"
            : "flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-[#FF00A4] transition-colors px-3 py-1.5 rounded-full border border-gray-200 hover:border-[#FF00A4]/40"
        }
      >
        <Globe className="w-5 h-5" />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          role="listbox"
          className={`absolute top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[100px] z-50 ${
            isRTL ? "left-0" : "right-0"
          }`}
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              role="option"
              aria-selected={lang === l.code}
              onClick={() => {
                if (lang === l.code) {
                  setOpen(false);
                  return;
                }
                onSwitchStart?.();
                setLang(l.code);
                setOpen(false);
              }}
              className={`w-full px-4 py-2.5 text-sm hover:bg-[#FFE1EC]/50 transition-colors flex items-center justify-between ${
                isRTL ? "text-right" : "text-left"
              } ${
                lang === l.code
                  ? "text-[#FF00A4] font-semibold bg-[#FFE1EC]/30"
                  : "text-gray-700"
              }`}
            >
              <span>{l.nativeLabel}</span>
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
}

/* ─── Section IDs for scroll-to-section ─── */
const MENO_SECTION_IDS = [
  "features",
  "how-it-works",
  "pricing",
  "faq",
] as const;
/* ─── Navbar (sub-header with scroll-to-section) ─── */
function Navbar({
  onLanguageSwitchStart,
}: {
  onLanguageSwitchStart?: () => void;
}) {
  const { t } = useLanguage();

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <nav className="flex h-14 pt-10 items-center justify-center lg:h-16">
      <div
        className={`custom-container flex w-full items-center gap-2 whitespace-nowrap px-2 justify-center sm:gap-3 xl:justify-end`}
      >
        <button
          type="button"
          onClick={() => scrollToSection("how-it-works")}
          className="rounded-full px-3 py-1.5 text-xs font-medium text-white/95 transition-colors hover:bg-white/10 sm:text-sm"
        >
          {t("nav.howItWorks")}
        </button>
        <button
          type="button"
          onClick={() => scrollToSection("features")}
          className="rounded-full px-3 py-1.5 text-xs font-medium text-white/95 transition-colors hover:bg-white/10 sm:text-sm"
        >
          {t("nav.features")}
        </button>
        <button
          type="button"
          onClick={() => scrollToSection("pricing")}
          className="rounded-full px-3 py-1.5 text-xs font-medium text-white/95 transition-colors hover:bg-white/10 sm:text-sm"
        >
          {t("nav.pricing")}
        </button>
        <button
          type="button"
          onClick={() => scrollToSection("faq")}
          className="rounded-full px-3 py-1.5 text-xs font-medium text-white/95 transition-colors hover:bg-white/10 sm:text-sm hidden sm:inline-flex"
        >
          {t("nav.faq")}
        </button>
        <a
          href="https://qhb.io/meno-help"
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full px-3 py-1.5 text-xs font-medium text-white/95 transition-colors hover:bg-white/10 sm:text-sm inline-flex items-center gap-2"
        >
          {t("nav.needHelp")}
        </a>
        <LanguageSwitcher
          variant="hero"
          onSwitchStart={onLanguageSwitchStart}
        />
      </div>
    </nav>
  );
}

/* ─── Main Page ─── */
export default function Meno({
  initialSeoTitle = "",
  initialSeoDescription = "",
  initialOgDescription = "",
  currentUrl: serverCurrentUrl = "",
  canonicalBaseUrl = "",
}: PageProps) {
  const router = useRouter();
  const { i18n } = useTranslation();
  const { t, isRTL, dir } = useLanguage();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  const textAlign = "text-start";
  const heroHeadlineClassName = `text-4xl sm:text-5xl lg:text-5xl xl:text-6xl ${
    isRTL ? "2xl:text-6xl" : "2xl:text-7xl"
  } font-extrabold text-white tracking-tight`;
  const heroHeadlineStyle = {
    fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
    lineHeight: isRTL ? 1.35 : 1.1,
  };
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [heroLottieData, setHeroLottieData] = useState<object | null>(null);
  const [currentUrl, setCurrentUrl] = useState(serverCurrentUrl || "");
  const [domainUrl, setDomainUrl] = useState("");
  const toggleFaq = (idx: number) =>
    setOpenFaqIndex((cur) => (cur === idx ? null : idx));

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.href) {
      setCurrentUrl(window.location.href);
      setDomainUrl(window.location.hostname);
    }
  }, []);

  useEffect(() => {
    if (!router.isReady) return;

    const routeLang = (router.query.lang as string) || "en";
    if (routeLang !== i18n.language) return;

    let cancelled = false;

    waitForLocaleReady().then(() => {
      if (!cancelled) setIsPageLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [router.isReady, router.query.lang, i18n.language]);

  const handleLanguageSwitchStart = () => {
    setIsPageLoading(true);
  };

  useEffect(() => {
    let isMounted = true;

    const loadHeroLottie = async () => {
      try {
        const response = await fetch("/images/meno/meno-opener.json");
        if (!response.ok) return;
        const data = await response.json();
        if (isMounted) setHeroLottieData(data);
      } catch {
        // Keep hero stable even if lottie asset fails to load.
      }
    };

    loadHeroLottie();
    return () => {
      isMounted = false;
    };
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: t("faq.q1"),
        acceptedAnswer: { "@type": "Answer", text: t("faq.a1") },
      },
      {
        "@type": "Question",
        name: t("faq.q2"),
        acceptedAnswer: { "@type": "Answer", text: t("faq.a2") },
      },
      {
        "@type": "Question",
        name: t("faq.q3"),
        acceptedAnswer: { "@type": "Answer", text: t("faq.a3") },
      },
      {
        "@type": "Question",
        name: t("faq.q4"),
        acceptedAnswer: { "@type": "Answer", text: t("faq.a4") },
      },
      {
        "@type": "Question",
        name: t("faq.q5"),
        acceptedAnswer: { "@type": "Answer", text: t("faq.a5") },
      },
      {
        "@type": "Question",
        name: t("faq.q6"),
        acceptedAnswer: { "@type": "Answer", text: t("faq.a6") },
      },
    ],
  };

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  const canonicalSiteUrl = (
    canonicalBaseUrl || `https://${domainUrl || "qrhub.io"}`
  ).replace(/\/+$/, "");
  const defaultOgImage = `${process.env.NEXT_PUBLIC_MAIN_WEBSITE_URL}/images/meno/meno-featured-image.png`;
  const effectiveUrl =
    currentUrl ||
    serverCurrentUrl ||
    `${canonicalSiteUrl}${router.asPath || ""}`;

  const title = initialSeoTitle || "Meno - Digital Menu & WhatsApp Ordering";
  const description =
    initialSeoDescription ||
    "Turn your menu into a sales machine. Manage all locations from one dashboard and take orders straight to WhatsApp.";

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={initialOgDescription} />
        <meta property="og:image" content={defaultOgImage} key="og-image" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={effectiveUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={initialOgDescription} />
        <meta
          name="twitter:image"
          content={defaultOgImage}
          key="twitter-image"
        />
      </Head>

      {isPageLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white">
          <CustomSpinner />
        </div>
      )}

      <div
        className={`min-h-screen w-full [scroll-behavior:smooth] ${isPageLoading ? "invisible" : "visible"}`}
        data-meno-motion-root
        dir={dir}
        style={{ fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif' }}
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />

        <div className="overflow-x-hidden">
          {/* ═══════════════════ HERO ═══════════════════ */}
          <div className="md:px-5 sm:px-4 lg:custom-container xl:mt-8">
            <section className="custom-container pt-8 pb-20 sm:pb-24 md:pb-16 lg:py-10 bg-meno-pink md:rounded-3xl overflow-hidden relative min-h-0 md:min-h-[680px] md:h-[680px] xl:h-[750px] xl:min-h-0">
              <div
                className={`pointer-events-none absolute -bottom-40 opacity-35 md:-bottom-36 h-[560px] w-[560px] md:opacity-40 xl:-bottom-28 xl:h-auto xl:w-[78%] xl:max-w-[1000px] xl:opacity-50 aspect-square z-0 ${
                  isRTL
                    ? "-left-48 md:-left-56 xl:-left-80"
                    : "-right-48 md:-right-56 xl:-right-80"
                }`}
              >
                <Image
                  src="/images/meno/meno-element.svg"
                  alt={t("hero.elementAlt")}
                  fill
                  className={`object-contain opacity-50 ${
                    isRTL ? "object-left-bottom" : "object-right-bottom"
                  }`}
                  aria-hidden="true"
                  priority
                />
              </div>
              <Navbar onLanguageSwitchStart={handleLanguageSwitchStart} />
              <div className={`relative z-10 text-center xl:text-start`}>
                <div
                  className={`flex flex-col items-center gap-12 xl:flex-row xl:items-start xl:gap-16  ${isRTL ? "xl:pr-20" : "xl:pl-20"} mt-[36px]`}
                >
                  <div className="flex-1 xl:text-start">
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.7,
                        ease: [0.25, 0.46, 0.45, 0.94],
                      }}
                      className="flex flex-col items-center xl:items-start"
                    >
                      <div className="mb-6 flex w-fit justify-center rounded-2xl bg-meno-light-purple p-4 xl:justify-start">
                        <Image
                          src="/images/meno/meno-logo.svg"
                          alt="Meno"
                          className="h-auto"
                          width={160}
                          height={80}
                          priority
                        />
                      </div>

                      <h1
                        className={`flex flex-col ${
                          isRTL ? "gap-[0.12em]" : "gap-[0.02em]"
                        }`}
                      >
                        <span
                          className={heroHeadlineClassName}
                          style={heroHeadlineStyle}
                        >
                          {t("hero.headline1")}
                        </span>
                        <span
                          className={heroHeadlineClassName}
                          style={heroHeadlineStyle}
                        >
                          {t("hero.headline2")}
                        </span>
                        <span
                          className={heroHeadlineClassName}
                          style={heroHeadlineStyle}
                        >
                          {t("hero.headline3")}{" "}
                          <span className="text-[#a3f0cd]">
                            {t("hero.headlineAccent")}
                          </span>
                        </span>
                      </h1>
                      <div className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-white xl:mx-0 xl:text-xl">
                        {t("hero.sub")}
                      </div>
                      <div
                        className={`mt-8 flex flex-col sm:flex-row gap-4 justify-center xl:justify-start ${isRTL ? "xl:justify-end" : ""}`}
                      >
                        <DashboardCreateLink
                          meno
                          className="inline-flex items-center justify-center gap-2 bg-black text-white font-bold text-base px-8 py-4 rounded-full transition-all hover:scale-105 active:scale-95 shadow-lg shadow-[#FF00A4]/25"
                          target="_blank"
                        >
                          {t("hero.cta")}
                          <ArrowIcon aria-hidden="true" className="w-5 h-5" />
                        </DashboardCreateLink>
                        <button
                          onClick={() => scrollToSection("how-it-works")}
                          className="inline-flex items-center justify-center gap-2 bg-white text-meno-pink font-semibold text-base px-8 py-4 rounded-full transition-all"
                        >
                          {t("hero.ctaSecondary")}
                        </button>
                      </div>
                      <div className="mt-4 mb-6 text-sm text-white/90 sm:mb-0">
                        {t("hero.trial")}
                      </div>
                    </motion.div>
                  </div>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      duration: 0.8,
                      delay: 0.1,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    className={`relative z-10 hidden flex-1 justify-center xl:flex ${isRTL ? "xl:justify-start" : "xl:justify-end"}`}
                  >
                    <div className="w-full max-w-[680px]">
                      <div className="relative h-[340px] w-full md:h-[420px] xl:h-[700px]">
                        {heroLottieData ? (
                          <div role="img" aria-label={t("hero.mockupAlt")}>
                            <Lottie
                              animationData={heroLottieData}
                              loop
                              autoplay
                              className="h-full w-full"
                            />
                          </div>
                        ) : (
                          <div className="h-full w-full" aria-hidden="true" />
                        )}
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </section>
          </div>
          {/* ═══════════════════ Business Type ═══════════════════ */}
          <BusinessType />
          {/* ═══════════════════ Steps ═══════════════════ */}
          <div id="how-it-works">
            <MenoSteps />
          </div>

          {/* ═══════════════════ FEATURES ═══════════════════ */}
          <section id="features" className="scroll-mt-20">
            <div className="custom-container">
              {/* ═══════════════════ Commission  ═══════════════════ */}
              <div className="overflow-hidden py-12">
                <section className="bg-meno-pink rounded-3xl border border-orange-100/50 overflow-hidden relative mt-10">
                  <div
                    className={`relative z-10 flex flex-col-reverse md:flex-row items-center md:min-h-[650px]`}
                  >
                    {/* TEXT CONTENT AREA */}
                    <FadeInSection
                      className={`lg:w-1/2 pb-4 p-8 sm:p-12 lg:p-20 ${textAlign}`}
                    >
                      <div
                        className={`inline-flex items-center gap-2 border-white/20 bg-white/20 text-white text-sm font-bold px-4 py-1.5 rounded-full mb-6`}
                      >
                        {t("f6.badge")}
                      </div>

                      <h2 className="font-extrabold text-gray-900">
                        <span
                          className="text-3xl md:text-4xl lg:text-6xl text-white"
                          style={{
                            fontFamily:
                              '"Inter", ui-sans-serif, system-ui, sans-serif',
                          }}
                        >
                          {t("f6.title1")}
                        </span>
                      </h2>

                      <div className="text-orange-50 text-lg leading-relaxed mt-4 mb-6 max-w-xl">
                        {t("f6.desc")}
                      </div>

                      {/* Feature Pills - Matches Analytics Style */}
                      <div className={`flex flex-wrap gap-3 mb-8`}>
                        {(["tag1", "tag2", "tag3", "tag4"] as const).map(
                          (key, i) => (
                            <div
                              key={i}
                              className="bg-white/15 backdrop-blur-sm border border-white/55 px-4 py-2 rounded-full flex items-center gap-2 shadow-sm"
                            >
                              <div className="w-2 h-2 rounded-full bg-white" />
                              <span className="text-sm font-bold text-white">
                                {t(`f6.${key}`)}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </FadeInSection>

                    {/* DYNAMIC VISUAL AREA */}
                    <FadeInSection
                      className="lg:w-1/2 w-full h-full py-6 sm:min-h-[500px] relative flex items-center justify-center px-4 sm:p-8 lg:p-0"
                      delay={0.2}
                    >
                      <div className="w-full flex-1 sm:flex justify-center">
                        <CommissionCountdown className="w-full max-w-[240px] sm:max-w-[500px] lg:max-w-[560px] mx-auto" />
                      </div>
                    </FadeInSection>
                  </div>
                </section>
              </div>

              {/* Feature 2: One Dashboard */}
              <FadeInSection>
                <div
                  className={`flex flex-col-reverse lg:flex-row items-stretch gap-12 py-24 lg:items-center lg:gap-20`}
                >
                  <div className="w-full flex-1">
                    <Image
                      src="/images/meno/outlets.webp"
                      alt="Outlets"
                      width={700}
                      height={352}
                      className="h-auto w-full"
                      priority
                    />
                  </div>
                  <div className={`w-full flex-1 ${textAlign}`}>
                    <div
                      className={`inline-flex items-center gap-2 bg-meno-orange/10 border border-meno-orange/20 text-meno-orange text-sm font-semibold px-4 py-1.5 rounded-full mb-4 ${isRTL ? "flex-row-reverse" : ""}`}
                    >
                      <LayoutDashboard
                        aria-hidden="true"
                        focusable="false"
                        className="w-4 h-4"
                      />
                      {t("f2.badge")}
                    </div>
                    <h2 className="font-extrabold text-gray-900">
                      <span
                        className="text-2xl sm:text-3xl lg:text-4xl text-gray-900"
                        style={{
                          fontFamily:
                            '"Inter", ui-sans-serif, system-ui, sans-serif',
                        }}
                      >
                        {t("f2.title1")}
                      </span>
                    </h2>
                    <div className="text-gray-600 text-lg leading-relaxed mt-4 mb-6">
                      {t("f2.desc1")}
                      <br />
                      {t("f2.desc2")}
                    </div>
                    <div
                      className={`flex flex-wrap gap-3 ${isRTL ? "justify-end" : ""}`}
                    >
                      <span className="bg-white border border-gray-200 text-gray-700 text-sm font-medium px-4 py-2 rounded-full">
                        {t("f2.tag1")}
                      </span>
                      <span className="bg-white border border-gray-200 text-gray-700 text-sm font-medium px-4 py-2 rounded-full">
                        {t("f2.tag2")}
                      </span>
                      <span className="bg-white border border-gray-200 text-gray-700 text-sm font-medium px-4 py-2 rounded-full">
                        {t("f2.tag3")}
                      </span>
                    </div>
                  </div>
                </div>
              </FadeInSection>

              {/* ═══════════════════ Your brand, your look ═══════════════════ */}
            </div>

            <MenoBrandLook />

            {/* ═══════════════════ Rich items that sell ═══════════════════ */}
            <MenoRichItems />

            <div className="custom-container">
              {/* Feature 3: WhatsApp Ordering */}
              <FadeInSection>
                <div className="relative mb-24 overflow-hidden rounded-3xl border border-[#25D366]/20 bg-[#075e54]">
                  <div
                    className={`flex flex-col items-stretch  lg:items-center lg:gap-8 xl:gap-12 ${isRTL ? "lg:flex-row-reverse" : "lg:flex-row"}`}
                  >
                    <div className="flex-1 block lg:hidden absolute -top-10 -end-10">
                      <Image
                        src={"/images/meno/order-by-whatsapp-mobile.svg"}
                        alt="WhatsApp ordering flow"
                        className="w-full h-60 opacity-70"
                        width={100}
                        height={100}
                        priority
                      />
                    </div>
                    <div
                      className={`z-10 w-full flex-1 p-6 sm:p-8 lg:basis-[56%] lg:py-10 ${textAlign} ${isRTL ? "lg:ps-0 lg:pe-12" : "lg:ps-12 lg:pe-0"}`}
                    >
                      <div
                        className={`inline-flex items-center gap-2 bg-[#aaffbb] text-[#075e54] text-sm font-semibold px-4 py-1.5 rounded-full mb-4 ${isRTL ? "flex-row-reverse" : ""}`}
                      >
                        <MessageCircle
                          aria-hidden="true"
                          focusable="false"
                          className="w-4 h-4"
                        />
                        {t("f3.badge")}
                      </div>
                      <h2 className="font-extrabold text-gray-900">
                        <span
                          className="text-3xl text-white lg:text-[2.6rem]"
                          style={{
                            fontFamily:
                              '"Inter", ui-sans-serif, system-ui, sans-serif',
                          }}
                        >
                          {t("f3.title1")}
                          <span className="block text-lg sm:text-xl lg:text-2xl font-semibold text-[#25d366]">
                            {t("f3.title2")}{" "}
                            <span className="text-lg sm:text-xl lg:text-2xl font-semibold text-[#25d366] block md:inline">
                              {t("f3.title3")}
                            </span>
                          </span>
                        </span>
                      </h2>
                      <div className="mb-6 mt-4 text-lg leading-relaxed text-white lg:text-[1.18rem]">
                        {t("f3.desc")}
                      </div>
                      <div className="text-sm sm:text-xl lg:text-2xl my-4 text-[#25d366]">
                        {t("f3.note2")}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
                        <div className="bg-black rounded-xl p-4 sm:p-5 shadow-sm">
                          <Link2
                            aria-hidden="true"
                            focusable="false"
                            className="w-8 h-8 text-[#ff8000] mb-3"
                          />
                          <div className="text-sm font-bold text-white">
                            {t("f3.payLink")}
                          </div>
                          <p style={{ lineHeight: 0 }}>
                            <span className="text-xs text-white/65 mt-1.5 block">
                              {t("f3.payLinkDesc")}
                            </span>
                          </p>
                        </div>
                        <div className="bg-black rounded-xl p-4 sm:p-5 shadow-sm">
                          <Banknote
                            aria-hidden="true"
                            focusable="false"
                            className="w-8 h-8 text-[#6ED3A5] mb-3"
                          />
                          <div className="text-sm font-bold text-white">
                            {t("f3.payCash")}
                          </div>
                          <p style={{ lineHeight: 0 }}>
                            <span className="text-xs text-white/65 mt-1.5 block">
                              {t("f3.payCashDesc")}
                            </span>
                          </p>
                        </div>
                        <div className="bg-black rounded-xl p-4 sm:p-5 shadow-sm">
                          <CreditCard
                            aria-hidden="true"
                            focusable="false"
                            className="w-8 h-8 text-[#00eeff] mb-3"
                          />
                          <div className="text-sm font-bold text-white">
                            {t("f3.payCard")}
                          </div>
                          <p style={{ lineHeight: 0 }}>
                            <span className="text-xs text-white/65 mt-1.5 block">
                              {t("f3.payCardDesc")}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="text-white/80 text-sm mt-4">
                        {t("f3.note")}
                      </div>
                    </div>
                    <div className="hidden flex-1 lg:block lg:basis-[44%]">
                      <Image
                        src="/images/meno/order-by-whatsapp.webp"
                        alt="WhatsApp ordering flow"
                        className="w-full lg:scale-125 xl:scale-100"
                        width={500}
                        height={600}
                        priority
                      />
                    </div>
                  </div>
                </div>
              </FadeInSection>
            </div>
          </section>

          {/* ═══════════════════  Analytics ═══════════════════ */}
          <MenoAnalytics />

          {/* ═══════════════════ OLD WAY vs MENO WAY ═══════════════════ */}
          {/* <MenoCompare /> */}

          {/* ═══════════════════ OLD WAY vs MENO WAY V2 ═══════════════════ */}
          <MenoCompareV2 />

          {/* ═══════════════════ Team and Roles ═══════════════════ */}
          <MenoTeamRoles />

          {/* ═══════════════════ PRICING ═══════════════════ */}
          {/* <MenoPricing /> */}

          {/* ═══════════════════ PRICING V2 ═══════════════════ */}
          <MenoPricingV2 />

          {/* ═══════════════════ QRHUB INTEGRATION ═══════════════════ */}
          <section className="py-16 lg:py-20 bg-white safari-paint-contain">
            <div className="custom-container">
              <FadeInSection>
                <div
                  className={`relative overflow-hidden bg-gray-900 border border-gray-800 rounded-3xl py-12 px-6 md:px-16 flex flex-col lg:flex-row items-center gap-8`}
                >
                  {/* Animated Background Orbs */}
                  <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500/10 blur-[100px] pointer-events-none" />
                  <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-500/10 blur-[100px] pointer-events-none" />

                  {/* Subtle Grid Pattern Overlay */}
                  <div
                    className="absolute inset-0 opacity-[0.03] pointer-events-none"
                    style={{
                      backgroundImage:
                        "radial-gradient(#fff 1px, transparent 1px)",
                      backgroundSize: "32px 32px",
                    }}
                  />

                  <div className="flex-1 relative z-10">
                    <h2 className="text-center lg:text-start">
                      <span
                        className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight"
                        style={{
                          fontFamily:
                            '"Inter", ui-sans-serif, system-ui, sans-serif',
                        }}
                      >
                        {t("qrhub.title")}
                      </span>
                    </h2>
                    <div className="text-gray-400 text-lg leading-relaxed mt-4 max-w-xl text-center lg:text-start">
                      {t("qrhub.desc")}
                    </div>
                  </div>

                  <div className="relative z-10 shrink-0">
                    <a
                      href="https://dashboard.qrhub.io/account/login?meno=true"
                      className="inline-flex items-center gap-3 bg-white text-gray-900 font-bold text-lg px-10 py-5 rounded-2xl hover:bg-gray-100 transition-all hover:scale-[1.03] active:scale-95 shadow-xl shadow-black/20 group"
                      target="_blank"
                    >
                      {t("qrhub.cta")}
                      <ArrowIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </FadeInSection>
            </div>
          </section>

          {/* ═══════════════════ FAQ ═══════════════════ */}
          <section
            id="faq"
            className="scroll-mt-20 bg-white py-20 lg:py-28 safari-paint-contain"
          >
            <div className="custom-container">
              <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:gap-16 xl:gap-24">
                <FadeInSection className="lg:w-[38%] lg:shrink-0">
                  <div className="text-start">
                    <span
                      className="mb-5 inline-flex items-center rounded-full bg-[#FFE1EC] px-4 py-1.5 text-xs font-bold text-[#FF00A4]"
                      style={{
                        fontFamily:
                          '"Inter", ui-sans-serif, system-ui, sans-serif',
                      }}
                    >
                      {t("faq.badge")}
                    </span>
                    <h2>
                      <span
                        className="text-3xl font-extrabold leading-tight text-gray-900 sm:text-4xl lg:text-5xl"
                        style={{
                          fontFamily:
                            '"Inter", ui-sans-serif, system-ui, sans-serif',
                        }}
                      >
                        {t("faq.title1")}{" "}
                        <span className="block text-[#FF00A4]">
                          {t("faq.titleAccent")}
                        </span>
                      </span>
                    </h2>
                    <p>
                      <span
                        className="mt-4 block text-base leading-relaxed text-gray-500 sm:text-lg"
                        style={{
                          fontFamily:
                            '"Inter", ui-sans-serif, system-ui, sans-serif',
                        }}
                      >
                        {t("faq.sub")}
                      </span>
                    </p>
                  </div>
                </FadeInSection>

                <FadeInSection delay={0.1} className="min-w-0 flex-1">
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <FAQItem
                      question={t("faq.q1")}
                      answer={t("faq.a1")}
                      isOpen={openFaqIndex === 0}
                      onToggle={() => toggleFaq(0)}
                    />
                    <FAQItem
                      question={t("faq.q2")}
                      answer={t("faq.a2")}
                      isOpen={openFaqIndex === 1}
                      onToggle={() => toggleFaq(1)}
                    />
                    <FAQItem
                      question={t("faq.q3")}
                      answer={t("faq.a3")}
                      isOpen={openFaqIndex === 2}
                      onToggle={() => toggleFaq(2)}
                    />
                    <FAQItem
                      question={t("faq.q4")}
                      answer={t("faq.a4")}
                      isOpen={openFaqIndex === 3}
                      onToggle={() => toggleFaq(3)}
                    />
                    <FAQItem
                      question={t("faq.q5")}
                      answer={t("faq.a5")}
                      isOpen={openFaqIndex === 4}
                      onToggle={() => toggleFaq(4)}
                    />
                    <FAQItem
                      question={t("faq.q6")}
                      answer={t("faq.a6")}
                      isOpen={openFaqIndex === 5}
                      onToggle={() => toggleFaq(5)}
                    />
                  </div>
                </FadeInSection>
              </div>
            </div>
          </section>

          {/* ═══════════════════ NEED HELP ═══════════════════ */}
          <section className="bg-white pb-20 safari-paint-contain">
            <div className="custom-container">
              <FadeInSection>
                <div
                  className={`relative overflow-hidden rounded-3xl border border-[#FF00A4]/15 bg-[#FFE1EC]/40 px-6 py-10 md:px-12 flex flex-col items-center gap-6 sm:flex-row sm:items-center `}
                >
                  <div className={`flex-1 ${textAlign}`}>
                    <div
                      className={`mb-4 inline-flex items-center gap-2 rounded-full border border-[#FF00A4]/20 bg-white/80 px-4 py-1.5 text-sm font-semibold text-[#FF00A4] `}
                    >
                      <MessageCircle
                        aria-hidden="true"
                        focusable="false"
                        className="h-4 w-4"
                      />
                      {t("needHelp.badge")}
                    </div>
                    <h2>
                      <span
                        className="text-2xl font-extrabold text-gray-900 sm:text-3xl"
                        style={{
                          fontFamily:
                            '"Inter", ui-sans-serif, system-ui, sans-serif',
                        }}
                      >
                        {t("needHelp.title")}
                      </span>
                    </h2>
                    <p>
                      <span
                        className="mt-3 block text-base leading-relaxed text-gray-600 sm:text-lg"
                        style={{
                          fontFamily:
                            '"Inter", ui-sans-serif, system-ui, sans-serif',
                        }}
                      >
                        {t("needHelp.desc")}
                      </span>
                    </p>
                  </div>

                  <div className="shrink-0">
                    <a
                      href="https://qhb.io/meno-help"
                      className="group inline-flex items-center gap-3 rounded-full bg-black px-8 py-4 text-base font-bold text-white transition-all hover:scale-105 active:scale-95 sm:text-lg"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("needHelp.cta")}
                      <ArrowIcon
                        aria-hidden="true"
                        className={`h-5 w-5 transition-transform ${isRTL ? "group-hover:-translate-x-1" : "group-hover:translate-x-1"}`}
                      />
                    </a>
                  </div>
                </div>
              </FadeInSection>
            </div>
          </section>

          {/* ═══════════════════ FINAL CTA ═══════════════════ */}
          <div className="custom-container">
            <section className="py-20 lg:py-28 bg-meno-pink rounded-3xl mb-20 safari-paint-contain">
              <div className="text-center px-6 md:px-10">
                <FadeInSection>
                  <h2 className="font-extrabold text-center">
                    <span
                      className="text-3xl sm:text-4xl lg:text-5xl text-white"
                      style={{
                        fontFamily:
                          '"Inter", ui-sans-serif, system-ui, sans-serif',
                      }}
                    >
                      {t("cta.title1")}
                      <span className="block">{t("cta.title2")}</span>
                    </span>
                  </h2>
                  <div className="text-white text-lg mb-10 mt-6 max-w-xl mx-auto">
                    {t("cta.sub")}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <DashboardCreateLink
                      meno
                      className="inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-white text-meno-pink font-bold text-base sm:text-lg px-6 sm:px-10 py-4 sm:py-5 rounded-full text-center leading-tight transition-all hover:scale-105 active:scale-95 shadow-xl"
                      target="_blank"
                    >
                      {t("cta.button")}
                      <ArrowIcon className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                    </DashboardCreateLink>
                    <button
                      type="button"
                      onClick={() => scrollToSection("how-it-works")}
                      className="inline-flex w-full sm:w-auto items-center justify-center gap-2 border-2 border-white text-white font-semibold text-base sm:text-lg px-6 sm:px-10 py-4 sm:py-5 rounded-full transition-all hover:bg-white/10"
                    >
                      {t("cta.buttonSecondary")}
                      <ArrowUp
                        className="w-4 h-4 sm:w-5 sm:h-5 shrink-0"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                  <div className="text-white text-sm mt-4">
                    {t("cta.trial")}
                  </div>
                </FadeInSection>
              </div>
            </section>
          </div>
        </div>

        <style jsx global>{`
          [data-meno-motion-root] {
            isolation: isolate;
          }

          [data-meno-motion-root] [data-framer-motion] {
            will-change: transform, opacity;
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
            transform-style: preserve-3d;
          }

          [data-meno-motion-root] .safari-paint-contain {
            contain: paint;
          }

          @supports (-webkit-touch-callout: none) {
            [data-meno-motion-root] .safari-paint-contain {
              contain: layout paint style;
              transform: translateZ(0);
              -webkit-transform: translateZ(0);
            }
          }
        `}</style>
      </div>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<PageProps> = async (
  context,
) => {
  const { lang } = context.params as { lang?: string };
  const language = lang === "ar" ? "ar" : "en";

  const protocol = context.req.headers["x-forwarded-proto"] || "https";
  const host = context.req.headers.host || "qrhub.io";
  const baseUrl = `${protocol}://${host}`;
  const resolvedUrl = context.resolvedUrl || `/${language}/meno-digital-menu`;
  const currentUrl = `${baseUrl}${resolvedUrl}`;
  const canonicalBaseUrl = (
    process.env.NEXT_PUBLIC_MAIN_WEBSITE_URL ||
    process.env.SITE_URL ||
    "https://qrhub.io"
  ).replace(/\/+$/, "");

  const t = i18next.getFixedT(language);

  const initialSeoTitle = t("seo.menoDigitalMenu.title");

  const initialSeoDescription = t("seo.menoDigitalMenu.description");
  const initialOgDescription = t("seo.menoDigitalMenu.ogDescription");

  return {
    props: {
      initialSeoTitle,
      initialSeoDescription,
      initialOgDescription,
      currentUrl,
      canonicalBaseUrl,
    },
  };
};
